import * as React from 'react';
import { cn } from '../../utils/cn';

type CodeTheme = 'light' | 'dark' | 'auto';

export interface CodeProps {
  children: string;
  language?: string;
  inline?: boolean;
  showLineNumbers?: boolean;
  highlightLines?: number[];
  copyButton?: boolean;
  theme?: CodeTheme;
  className?: string;
}

export const Code = React.forwardRef<HTMLElement, CodeProps>(
  (
    {
      children,
      language,
      inline = false,
      showLineNumbers = false,
      highlightLines = [],
      copyButton = false,
      theme = 'auto',
      className,
    },
    ref
  ) => {
    const [copied, setCopied] = React.useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(children);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };

    if (inline) {
      return (
        <code
          ref={ref as React.Ref<HTMLElement>}
          className={cn(
            'px-1.5 py-0.5 rounded text-sm font-mono',
            'bg-base-200 text-base-content',
            className
          )}
        >
          {children}
        </code>
      );
    }

    const lines = children.split('\n');
    const lineNumberWidth = lines.length.toString().length;

    return (
      <div className={cn('relative group', className)}>
        <pre
          ref={ref as React.Ref<HTMLPreElement>}
          className={cn(
            'overflow-x-auto p-4 rounded-lg text-sm',
            'bg-base-200 text-base-content',
            theme === 'dark' && 'bg-base-300',
            theme === 'auto' && 'dark:bg-base-300',
            showLineNumbers && 'pl-12'
          )}
        >
          {showLineNumbers && (
            <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col py-4 text-right pr-3 select-none">
              {lines.map((_, index) => (
                <span
                  key={index}
                  className={cn(
                    'text-base-content/50 text-xs leading-6 font-mono',
                    highlightLines.includes(index + 1) && 'text-primary font-semibold'
                  )}
                  style={{ minWidth: `${lineNumberWidth}ch` }}
                >
                  {index + 1}
                </span>
              ))}
            </div>
          )}
          <code className={cn('font-mono', language && `language-${language}`)}>
            {lines.map((line, index) => (
              <div
                key={index}
                className={cn(
                  'leading-6',
                  highlightLines.includes(index + 1) && 'bg-primary/10 -mx-4 px-4'
                )}
              >
                {line || '\n'}
              </div>
            ))}
          </code>
        </pre>

        {copyButton && (
          <button
            onClick={handleCopy}
            className={cn(
              'absolute top-2 right-2',
              'btn btn-sm btn-ghost',
              'opacity-0 group-hover:opacity-100 transition-opacity',
              'focus:opacity-100'
            )}
            aria-label="Copy code"
          >
            {copied ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-success">
                <path
                  d="M13.5 4.5L6 12L2.5 8.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect
                  x="5.5"
                  y="5.5"
                  width="8"
                  height="8"
                  rx="1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M10.5 5.5V3.5C10.5 2.94772 10.0523 2.5 9.5 2.5H3.5C2.94772 2.5 2.5 2.94772 2.5 3.5V9.5C2.5 10.0523 2.94772 10.5 3.5 10.5H5.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            )}
          </button>
        )}

        {language && (
          <div className="absolute top-2 left-2 text-xs text-base-content/50 font-mono">
            {language}
          </div>
        )}
      </div>
    );
  }
);

Code.displayName = 'Code';
